// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useEffect, useRef } from 'react';

import { getWindow } from '@change-corgi/core/window';

import type { QrCodeTracking, QrViewCloseReason } from '#src/app/pages/psf/promoteOrShare/shared/hooks';

type Params = Readonly<{
	tracking: Pick<QrCodeTracking, 'trackViewed' | 'trackClosed'>;
	/** The slide-up's open state. The view ends as soon as it turns false, not after the exit animation. */
	isSlideUpOpen: boolean;
	/** Why the view is unmounting (back, or poster once the poster view exists), or `null` when nothing said, which ends it as `close`. */
	getExitReason: () => QrViewCloseReason | null;
	/** Whether a Save share sheet is open, read when the page is hidden. */
	isShareInFlight: () => boolean;
}>;

/** The view of this mount: when it started, and whether its close event fired. */
type View = { startedAt: number; ended: boolean };

/**
 * One QR view per mount: `psf_qr_view` when the view mounts, and one `psf_qr_close` with the dwell
 * time on the first exit, whichever comes first: back, the slide-up closing, `pagehide`, or the page
 * being hidden while no Save share sheet is open. Print poster will be an exit once the poster view exists. The iOS share and print sheets can hide
 * the page, so a hide during a share does not end the view. Once ended, the view fires nothing more,
 * even when the supporter comes back to it. A new mount is a new view.
 */
export function useQrViewTracking({ tracking, isSlideUpOpen, getExitReason, isShareInFlight }: Params): void {
	// Latest-ref, so a new `tracking` identity (the share tracking data changed) restarts no listener
	// and ends no view.
	const latestRef = useRef({ tracking, getExitReason, isShareInFlight });
	useEffect(() => {
		latestRef.current = { tracking, getExitReason, isShareInFlight };
	});

	const viewRef = useRef<View | null>(null);
	const mountedRef = useRef(false);

	const endView = useCallback((reason: QrViewCloseReason) => {
		const view = viewRef.current;
		if (!view || view.ended) return;
		view.ended = true;
		latestRef.current.tracking.trackClosed(reason, Date.now() - view.startedAt);
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		// Kept across StrictMode's effect re-run, so the view starts and fires once per mount.
		if (!viewRef.current) {
			viewRef.current = { startedAt: Date.now(), ended: false };
			latestRef.current.tracking.trackViewed();
		}

		const win = getWindow();
		const onPageHide = () => endView('page_hide');
		const onVisibilityChange = () => {
			if (win.document.visibilityState === 'hidden' && !latestRef.current.isShareInFlight()) {
				endView('visibility_hidden');
			}
		};
		win.addEventListener('pagehide', onPageHide);
		win.document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			win.removeEventListener('pagehide', onPageHide);
			win.document.removeEventListener('visibilitychange', onVisibilityChange);
			mountedRef.current = false;
			// Read now: the next QR view open resets it.
			const reason = latestRef.current.getExitReason() ?? 'close';
			// Deferred, so StrictMode's simulated unmount, which re-runs the effect straight away, ends nothing.
			queueMicrotask(() => {
				if (!mountedRef.current) endView(reason);
			});
		};
	}, [endView]);

	useEffect(() => {
		if (!isSlideUpOpen) endView('close');
	}, [isSlideUpOpen, endView]);
}
